import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Injecte le token JWT dans chaque requête
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('printcorp_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Gestion globale des erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('printcorp_token');
      localStorage.removeItem('printcorp_user');
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── AUTH ──────────────────────────────────────────
export const authAPI = {
  login:          (data) => api.post('/auth/login', data),
  me:             ()     => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// ── PRODUITS ──────────────────────────────────────
export const productAPI = {
  list:          (params)     => api.get('/products', { params }),
  get:           (id)         => api.get(`/products/${id}`),
  getBySlug:     (slug)       => api.get(`/products/slug/${slug}`),
  create:        (data)       => api.post('/products', data),
  bulkImport:      (data) => api.post('/products/import', data),
  applyPackshots: ()     => api.post('/products/apply-packshots'),
  update:        (id, d)      => api.put(`/products/${id}`, d),
  delete:        (id)         => api.delete(`/products/${id}`),
  togglePublish: (id)         => api.patch(`/products/${id}/publish`),
  getImages:     (id)         => api.get(`/products/${id}/images`),
  addImage:      (id, data)   => api.post(`/products/${id}/images`, data),
  deleteImage:   (id, imgId)  => api.delete(`/products/${id}/images/${imgId}`),
  reorderImages: (id, data)   => api.put(`/products/${id}/images/reorder`, data),
};

// ── CATÉGORIES ────────────────────────────────────
export const categoryAPI = {
  list:   (params) => api.get('/categories', { params }),
  create: (data)   => api.post('/categories', data),
  update: (id, d)  => api.put(`/categories/${id}`, d),
  delete: (id)     => api.delete(`/categories/${id}`),
};

// ── TECHNIQUES ────────────────────────────────────
export const servicesAPI = {
  list:    (all = false) => api.get('/services', { params: all ? { all: 1 } : {} }),
  listAll: ()            => api.get('/services', { params: { all: 1 } }),
  create:  (data)        => api.post('/services', data),
  update:  (id, d)       => api.put(`/services/${id}`, d),
  delete:  (id)          => api.delete(`/services/${id}`),
};

export const sizeAPI = {
  list:    (all = false) => api.get('/product-sizes', { params: all ? { all: 1 } : {} }),
  listAll: ()            => api.get('/product-sizes', { params: { all: 1 } }),
  create:  (data)        => api.post('/product-sizes', data),
  update:  (id, d)       => api.put(`/product-sizes/${id}`, d),
  delete:  (id)          => api.delete(`/product-sizes/${id}`),
};

export const techniqueAPI = {
  list:    (all = false) => api.get('/techniques', { params: all ? { all: 1 } : {} }),
  listAll: ()            => api.get('/techniques', { params: { all: 1 } }),
  create:  (data)        => api.post('/techniques', data),
  update:  (id, d)       => api.put(`/techniques/${id}`, d),
  delete:  (id)          => api.delete(`/techniques/${id}`),
};

// ── MAQUETTES ─────────────────────────────────────
export const mockupAPI = {
  list:   (params) => api.get('/mockups', { params }),
  unused: ()       => api.get('/mockups/unused'),
  get:    (id)     => api.get(`/mockups/${id}`),
  create: (data)   => api.post('/mockups', data),
  update: (id, d)  => api.put(`/mockups/${id}`, d),
  delete: (id)     => api.delete(`/mockups/${id}`),
};

// ── DÉLAIS ────────────────────────────────────────
export const delayAPI = {
  list:    ()      => api.get('/production-delays'),
  listAll: ()      => api.get('/production-delays?all=1'),
  create:  (d)     => api.post('/production-delays', d),
  update:  (id, d) => api.put(`/production-delays/${id}`, d),
  delete:  (id)    => api.delete(`/production-delays/${id}`),
};

// ── CLIENTS ───────────────────────────────────────
export const clientAPI = {
  list:          (params) => api.get('/clients', { params }),
  get:           (id)     => api.get(`/clients/${id}`),
  create:        (data)   => api.post('/clients', data),
  update:        (id, d)  => api.put(`/clients/${id}`, d),
  delete:        (id)     => api.delete(`/clients/${id}`),
  updateBalance: (id, d)  => api.patch(`/clients/${id}/balance`, d),
};

// ── COMMANDES ─────────────────────────────────────
export const orderAPI = {
  list:         (params) => api.get('/orders', { params }),
  get:          (id)     => api.get(`/orders/${id}`),
  submit:       (data)   => api.post('/orders', data),
  updateStatus: (id, d)  => api.patch(`/orders/${id}/status`, d),
  markRead:     (id)     => api.patch(`/orders/${id}/read`),
  delete:       (id)     => api.delete(`/orders/${id}`),
};

// ── DEVIS ─────────────────────────────────────────
export const quoteAPI = {
  list:         (params) => api.get('/quotes', { params }),
  get:          (id)     => api.get(`/quotes/${id}`),
  create:       (data)   => api.post('/quotes', data),
  fromOrder:    (data)   => api.post('/quotes/from-order', data),
  update:       (id, d)  => api.put(`/quotes/${id}`, d),
  updateStatus: (id, d)  => api.patch(`/quotes/${id}/status`, d),
  convert:      (id)     => api.post(`/quotes/${id}/convert`),
  delete:       (id)     => api.delete(`/quotes/${id}`),
  fetchPdf:     (id)     => api.get(`/quotes/${id}/pdf`,          { responseType: 'text' }),
  downloadPdf:  (id)     => api.get(`/quotes/${id}/pdf-download`,  { responseType: 'blob' }),
};

// ── FACTURES ──────────────────────────────────────
export const invoiceAPI = {
  list:          (params)  => api.get('/invoices', { params }),
  get:           (id)      => api.get(`/invoices/${id}`),
  create:        (data)    => api.post('/invoices', data),
  update:        (id, d)   => api.put(`/invoices/${id}`, d),
  delete:        (id)      => api.delete(`/invoices/${id}`),
  addPayment:    (id, d)   => api.post(`/invoices/${id}/payment`, d),
  deletePayment: (id, pid) => api.delete(`/invoices/${id}/payments/${pid}`),
  markSent:      (id)      => api.patch(`/invoices/${id}/sent`),
  fetchPdf:      (id)      => api.get(`/invoices/${id}/pdf`,         { responseType: 'text' }),
  downloadPdf:   (id)      => api.get(`/invoices/${id}/pdf-download`, { responseType: 'blob' }),
  downloadXml:      (id)   => api.get(`/invoices/${id}/xml`,              { responseType: 'blob' }),
  downloadFacturX:  (id)   => api.get(`/invoices/${id}/facturx-download`, { responseType: 'blob' }),
};

// ── BLOG ──────────────────────────────────────────
export const blogAPI = {
  list:       (params) => api.get('/blog', { params }),
  get:        (slug)   => api.get(`/blog/${slug}`),
  categories: ()       => api.get('/blog/categories'),
  create:     (data)   => api.post('/blog', data),
  update:     (id, d)  => api.put(`/blog/${id}`, d),
  delete:     (id)     => api.delete(`/blog/${id}`),
};

// ── NEWSLETTER ────────────────────────────────────
export const newsletterAPI = {
  subscribe:        (data)    => api.post('/newsletter/subscribe', data),
  reactivate:       (data)    => api.post('/newsletter/reactivate', data),
  subscribers:      (params)  => api.get('/newsletter/subscribers', { params }),
  updateSubscriber: (id, d)   => api.put(`/newsletter/subscribers/${id}`, d),
  deleteSubscriber: (id)      => api.delete(`/newsletter/subscribers/${id}`),
  unsubscribe:      (token)   => api.get('/newsletter/unsubscribe', { params: { token } }),
  campaigns:        ()        => api.get('/newsletter/campaigns'),
  createCampaign:   (data)    => api.post('/newsletter/campaigns', data),
  sendCampaign:     (id, data)  => api.post(`/newsletter/campaigns/${id}/send`, data || {}),
  deleteCampaign:   (id)        => api.delete(`/newsletter/campaigns/${id}`),
};

// ── CONTACT ───────────────────────────────────────
export const contactAPI = {
  submit:             (data)     => api.post('/contact', data),
  quickQuote:         (data)     => api.post('/contact/quick-quote', data),
  messages:           (p)        => api.get('/contact/messages', { params: p }),
  markRead:           (id)       => api.patch(`/contact/messages/${id}/read`),
  deleteMessage:      (id)       => api.delete(`/contact/messages/${id}`),
  quickQuotes:        ()         => api.get('/contact/quick-quotes'),
  markQuoteRead:      (id)       => api.patch(`/contact/quick-quotes/${id}/read`),
  updateQuoteStatus:  (id, data) => api.patch(`/contact/quick-quotes/${id}/status`, data),
  deleteQuickQuote:   (id)       => api.delete(`/contact/quick-quotes/${id}`),
};

// ── RÉSEAUX SOCIAUX ────────────────────────────────
export const socialAPI = {
  list:    ()        => api.get('/social-networks'),
  listAll: ()        => api.get('/social-networks/all'),
  create:  (data)    => api.post('/social-networks', data),
  update:  (id, data)=> api.put(`/social-networks/${id}`, data),
  delete:  (id)      => api.delete(`/social-networks/${id}`),
};

// ── MÉDIAS ────────────────────────────────────────
export const mediaAPI = {
  list:       (params)  => api.get('/media', { params }),
  unused:     ()        => api.get('/media/unused'),
  upload:     (form)    => api.post('/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkDelete: (ids)     => api.delete('/media/bulk', { data: { ids } }),
  delete:     (id)      => api.delete(`/media/${id}`),
};

// ── PARAMÈTRES ────────────────────────────────────
export const settingsAPI = {
  public:     ()       => api.get('/settings/public'),
  all:        ()       => api.get('/settings'),
  update:     (data)   => api.put('/settings', data),
  users:      ()       => api.get('/settings/users'),
  createUser: (data)   => api.post('/settings/users', data),
  updateUser: (id, d)  => api.put(`/settings/users/${id}`, d),
  deleteUser: (id)     => api.delete(`/settings/users/${id}`),
};

// ── FAQ ───────────────────────────────────────────
export const faqAPI = {
  list:   ()       => api.get('/faq'),
  create: (data)   => api.post('/faq', data),
  update: (id, d)  => api.put(`/faq/${id}`, d),
  delete: (id)     => api.delete(`/faq/${id}`),
};

// ── NOTIFICATIONS ─────────────────────────────────
export const notificationAPI = {
  counts: () => api.get('/notifications/counts'),
};

// ── RAPPORTS ──────────────────────────────────────
export const reportAPI = {
  dashboard:   (params) => api.get('/reports/dashboard', { params }),
  prospecting: ()       => api.get('/reports/prospecting'),
};

// ── TÉMOIGNAGES ───────────────────────────────────
export const testimonialAPI = {
  list:   (params) => api.get('/testimonials', { params }),
  create: (data)   => api.post('/testimonials', data),
  update: (id, d)  => api.put(`/testimonials/${id}`, d),
  delete: (id)     => api.delete(`/testimonials/${id}`),
};

// ── ESTIMATION ────────────────────────────────────
export const estimateAPI = {
  calculate: (data) => api.post('/estimate', data),
};


// ── Email Templates ────────────────────────────────────────────────────────
export const emailTemplateAPI = {
  list:    ()       => api.get('/email-templates'),
  get:     (id)     => api.get(`/email-templates/${id}`),
  create:  (data)   => api.post('/email-templates', data),
  update:  (id, d)  => api.put(`/email-templates/${id}`, d),
  delete:  (id)     => api.delete(`/email-templates/${id}`),
  send:    (id, d)  => api.post(`/email-templates/${id}/send`, d),
  sendDirect: (d)   => api.post('/email-send', d),
  testSmtp:  (d)    => api.post('/email-test', d),
  logs:      ()     => api.get('/email-logs'),
  clearLogs: ()     => api.delete('/email-logs'),
};

// ── Signature électronique ─────────────────────────────────────────────────
export const signatureAPI = {
  createRequest: (quoteId, data)  => api.post(`/quotes/${quoteId}/signature-request`, data),
  getStatus:     (quoteId)        => api.get(`/quotes/${quoteId}/signature`),
  getSignPage:   (token)          => api.get(`/sign/${token}`),
  submitSign:    (token, data)    => api.post(`/sign/${token}`, data),
  list:          ()               => api.get('/signatures'),
  getProof:      (id)             => api.get(`/signatures/${id}/proof`),
  delete:        (id)             => api.delete(`/signatures/${id}`),
};

// ── POPUPS SITE ───────────────────────────────────────────────────────────
export const popupAPI = {
  listActive: ()      => api.get('/popups'),
  listAll:    ()      => api.get('/popups/all'),
  create:     (data)  => api.post('/popups', data),
  update:     (id, d) => api.put(`/popups/${id}`, d),
  delete:     (id)    => api.delete(`/popups/${id}`),
};

// ── Extranets Client ───────────────────────────────────────────────────────
export const extranetAPI = {
  list:          ()              => api.get('/extranets'),
  get:           (id)            => api.get(`/extranets/${id}`),
  create:        (data)          => api.post('/extranets', data),
  update:        (id, data)      => api.put(`/extranets/${id}`, data),
  delete:        (id)            => api.delete(`/extranets/${id}`),
  addProduct:    (id, data)      => api.post(`/extranets/${id}/products`, data),
  removeProduct: (id, pid)       => api.delete(`/extranets/${id}/products/${pid}`),
  sendAccess:    (id, data)      => api.post(`/extranets/${id}/send`, data),
  sendBat:       (id, data)      => api.post(`/extranets/${id}/send-bat`, data),
  toggleStatus:  (id)            => api.post(`/extranets/${id}/toggle-status`),
  close:         (id)            => api.post(`/extranets/${id}/close`),
  uploadFile:    (id, formData)  => api.post(`/extranets/${id}/upload-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  getFiles:      (id)            => api.get(`/extranets/${id}/files`),
  deleteFile:    (id, fid)       => api.delete(`/extranets/${id}/files/${fid}`),
  stepBack:      (id)            => api.post(`/extranets/${id}/step-back`),
  getBatProof:   (id)            => api.get(`/extranets/${id}/bat-proof`),
  // Public (no auth)
  portal:        (token)         => api.get(`/client-portal/${token}`),
  select:        (token, data)   => api.post(`/client-portal/${token}/select`, data),
  signBat:       (token, data)   => api.post(`/client-portal/${token}/bat`, data),
  clientStepBack:(token)         => api.post(`/client-portal/${token}/step-back`),
};

export default api;